import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, Building, MonitorDot, Boxes, Store, ChefHat, BookOpen,
         FileBarChart, Wallet, ScrollText, LogOut, Menu, X, Loader2, QrCode, Landmark } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Logo } from "../../components/Brand";

import CorporateDashboard from "./CorporateDashboard";
import MallDashboard from "./MallDashboard";
import FoodCourtCommand from "./FoodCourtCommand";
import GroupDashboard from "./GroupDashboard";
import RestaurantDashboard from "./RestaurantDashboard";
import KDS from "./KDS";
import MenuManager from "./MenuManager";
import Reports from "./Reports";
import AuditLog from "./AuditLog";
import TableQR from "./TableQR";
import PaymentsConfig from "./PaymentsConfig";

const NAV = [
  { key: "corporate", label: "Corporate", icon: Building2, roles: ["super_admin", "corporate_admin", "finance", "analyst"] },
  { key: "mall", label: "Mall", icon: Building, roles: ["super_admin", "corporate_admin", "mall_admin", "finance", "analyst"] },
  { key: "command", label: "Food Court Command", icon: MonitorDot, roles: ["super_admin", "corporate_admin", "mall_admin", "food_court_manager", "support", "analyst"] },
  { key: "group", label: "Restaurant Group", icon: Boxes, roles: ["super_admin", "corporate_admin", "restaurant_group_admin", "analyst"] },
  { key: "restaurant", label: "Restaurant", icon: Store, roles: ["super_admin", "corporate_admin", "mall_admin", "restaurant_group_admin", "restaurant_admin", "analyst"] },
  { key: "kds", label: "Kitchen Display", icon: ChefHat, roles: ["super_admin", "corporate_admin", "food_court_manager", "restaurant_group_admin", "restaurant_admin", "restaurant_operator"] },
  { key: "menu", label: "Menu Manager", icon: BookOpen, roles: ["super_admin", "corporate_admin", "restaurant_group_admin", "restaurant_admin"] },
  { key: "tables", label: "Table QRs", icon: QrCode, roles: ["super_admin", "corporate_admin", "mall_admin", "food_court_manager", "restaurant_group_admin"] },
  { key: "dsr", label: "Daily Sales (DSR)", icon: FileBarChart, roles: ["super_admin", "corporate_admin", "mall_admin", "food_court_manager", "finance", "analyst"] },
  { key: "settlement", label: "Settlements", icon: Wallet, roles: ["super_admin", "corporate_admin", "restaurant_group_admin", "finance"] },
  { key: "payments", label: "Payments & Route", icon: Landmark, roles: ["super_admin", "corporate_admin", "finance"] },
  { key: "audit", label: "Audit & Alerts", icon: ScrollText, roles: ["super_admin", "corporate_admin", "mall_admin", "support", "finance"] },
];

const ROLE_LABEL = {
  super_admin: "Super Admin", corporate_admin: "Corporate Admin", mall_admin: "Mall Admin",
  food_court_manager: "Food Court Manager", restaurant_group_admin: "Group Admin",
  restaurant_admin: "Restaurant Admin", restaurant_operator: "Kitchen Operator",
  finance: "Finance", support: "Support", analyst: "Analyst",
};

export default function AdminShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [ctx, setCtx] = useState(null);
  const [nav, setNav] = useState([]);
  const [active, setActive] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get("/admin/context").then((r) => {
      setCtx(r.data);
      const allowed = NAV.filter((n) => n.roles.includes(r.data.role));
      setNav(allowed);
      setActive(allowed[0]?.key);
    });
  }, []);

  if (!ctx || !active) {
    return <div className="min-h-screen bg-[#08183A] flex items-center justify-center"><Loader2 className="animate-spin text-amber-400" size={28} /></div>;
  }

  const render = () => {
    const props = { ctx, user };
    switch (active) {
      case "corporate": return <CorporateDashboard {...props} />;
      case "mall": return <MallDashboard {...props} />;
      case "command": return <FoodCourtCommand {...props} />;
      case "group": return <GroupDashboard {...props} />;
      case "restaurant": return <RestaurantDashboard {...props} />;
      case "kds": return <KDS {...props} />;
      case "menu": return <MenuManager {...props} />;
      case "tables": return <TableQR {...props} />;
      case "payments": return <PaymentsConfig {...props} />;
      case "dsr": return <Reports {...props} mode="dsr" />;
      case "settlement": return <Reports {...props} mode="settlement" />;
      case "audit": return <AuditLog {...props} />;
      default: return null;
    }
  };

  const Sidebar = (
    <div className="w-64 bg-[#0E2650] border-r hairline p-4 flex flex-col justify-between shrink-0 h-full">
      <div>
        <div className="mb-6"><Logo size="sm" /></div>
        <nav className="space-y-1">
          {nav.map((n) => (
            <button key={n.key} onClick={() => { setActive(n.key); setOpen(false); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${active === n.key ? "gold-gradient text-slate-950" : "text-slate-400 hover:bg-white/5 hover:text-slate-100"}`}
              data-testid={`nav-${n.key}`}>
              <n.icon size={17} /> {n.label}
            </button>
          ))}
        </nav>
      </div>
      <div className="card-surface-2 rounded-xl p-3">
        <div className="text-sm font-semibold text-slate-100 truncate">{user.name}</div>
        <div className="text-[11px] text-amber-400">{ROLE_LABEL[user.role]}</div>
        <button onClick={() => { logout(); navigate("/login"); }}
          className="mt-2 w-full flex items-center justify-center gap-2 text-xs card-surface rounded-lg py-2 text-slate-300 hover:text-red-400 transition-colors" data-testid="logout-btn">
          <LogOut size={13} /> Sign Out
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#08183A] flex text-slate-100">
      <div className="hidden md:block h-screen sticky top-0 print:hidden">{Sidebar}</div>

      {open && (
        <div className="fixed inset-0 z-50 md:hidden print:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-0 h-full animate-fadeup">{Sidebar}</div>
        </div>
      )}

      <div className="flex-1 min-w-0">
        <div className="md:hidden sticky top-0 z-40 glass px-4 py-3 flex items-center justify-between print:hidden">
          <button onClick={() => setOpen(true)} data-testid="mobile-menu-btn"><Menu size={22} /></button>
          <Logo size="sm" />
          <button onClick={() => { logout(); navigate("/login"); }}><LogOut size={18} /></button>
        </div>
        <div className="p-4 md:p-8 max-w-[1600px] mx-auto">{render()}</div>
      </div>
    </div>
  );
}
